import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  IconArrowLeft,
  IconEmail,
  IconSchedulingLink,
} from '@chilipiper/icons/figma-generated/src/icon'
import { SvgIconInstantBooker } from '@chilipiper/icons'
import { IconGoogle } from '@chilipiper/icons/figma-generated/src/logo/Google'
import { Box, css, Flex, Grid, styled, Text } from '@chilipiper/design-system'
import { Button, useShowToast } from '@chilipiper/design-system/src/new'
import { captureException } from '@chilipiper/sentry'
import { OnboardingChiliPiperLogo, Progress } from '@chilipiper/onboarding'
import { routes } from '../../../routes/routes'
import SvgCircleCut from '../../../components/calendar/home/SvgCircleCut'
import { Link } from '../../../components/link/Link'
import { useFinishOnboarding } from '../../../api/workspace'
import { Spacer } from '../components/Spacer'
import { useIsDesktop } from '../useIsDesktop'
import { useOnboardingExtraSteps } from '../steps'
import { FinishingOnboarding } from '../components/FinishingOnboarding'
import smartCalendar from './images/smartCalendar.svg'
import autoDetect from './images/autoDetect.png'
import suggestedTimes from './images/suggestedTimes.png'
import command from './images/command.png'
import priority from './images/priority.png'
import starMask from './images/starMask.svg'
import { Feature } from './Feature'
import { useTrackFeature } from '@/hooks/useTracking'
import { chromeExtensionWebStoreUrl } from '@/utils/extension'
import { ChilicalEvent, ChilicalFeature } from '@/tracking/events'

const features = [
  {
    text: 'Quickly share availability with  Auto Suggested Times',
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- no figma-generated Instant Booker icon yet
    icon: SvgIconInstantBooker,
    image: suggestedTimes,
  },
  {
    text: 'Keep your scheduling links at your fingertips with ChiliCommand',
    icon: IconSchedulingLink,
    image: command,
  },
  {
    text: 'Add priority to important meetings in your calendar',
    icon: SvgCircleCut,
    image: priority,
  },
  {
    text: 'Auto-detect scheduling needs when composing emails',
    icon: IconEmail,
    image: autoDetect,
  },
]
export const Extension = () => {
  const [selectedFeature, setSelectedFeature] = useState(0)
  const isDesktop = useIsDesktop()
  const { mutateAsync: finishOnboarding, isPending: isUpdatingOnboarding } = useFinishOnboarding()
  const showToast = useShowToast()
  const navigate = useNavigate()
  const track = useTrackFeature()
  const extraSteps = useOnboardingExtraSteps()
  const hasEmailStep = extraSteps > 0

  const handleOnboardingFinished = () => {
    if (hasEmailStep) {
      navigate(routes.onboarding.email)
      return
    }
    finishOnboarding()
      .then(() => {
        track(ChilicalEvent.OnboardingFinished, ChilicalFeature.Onboarding)
        navigate(`${routes.meetingTypeList}`)
      })
      .catch(error => {
        captureException(new Error('Failed to finish onboarding', { cause: error }))
        showToast({
          title: 'Failed to finish onboarding',
          variant: 'caution',
        })
      })
  }

  if (isUpdatingOnboarding) return <FinishingOnboarding />

  return (
    <Flex h='100%' w='100%' bg='bg/background-gradient'>
      <StyledFlex
        flexDirection='column'
        alignItems={['center', 'center', 'flex-start']}
        bg='bg/minor'
        flex='1 0 50%'
        overflow={['auto', 'auto', 'initial']}
        px={6}
        py={5}
        position='relative'
      >
        <OnboardingChiliPiperLogo />
        <Spacer />
        <Box maxWidth='648px' mx='auto'>
          <Flex
            mb={[9, 9, 6]}
            alignItems={['center', 'center', 'flex-start']}
            flexDirection='column'
          >
            <Flex alignItems='baseline' mb={2}>
              <Text fontSize={[20, 20, 32]} fontWeight={600} color='fg/major'>
                Smart scheduling
              </Text>
              <StyledBox
                boxShadow='border/section'
                bg='bg/section'
                borderRadius={24}
                ml={4}
                display={['none', 'none', 'block']}
              >
                <img src={smartCalendar} alt='' height={69} width={69} />
              </StyledBox>
            </Flex>
            <Text fontSize={[20, 20, 32]} fontWeight={600} color='fg/major'>
              with our spicy extension
            </Text>
          </Flex>
          <Flex flexDirection='column' maxWidth='648px' mx='auto'>
            <Grid gridTemplateColumns={['1fr', '1fr', '1fr 1fr']} gap={2} mb={isDesktop ? 6 : 9}>
              {features.map(({ text, icon }, index) => {
                return (
                  <Feature
                    key={text}
                    icon={icon}
                    text={text}
                    isSelected={selectedFeature === index}
                    onClick={() => setSelectedFeature(index)}
                  />
                )
              })}
            </Grid>
            <Flex justifyContent='space-between' gap={4} mb={9}>
              <Link to={routes.onboarding.location}>
                {linkProps => (
                  <Button
                    variant='minor'
                    startContent={<Button.StartContent variant='icon' icon={IconArrowLeft} />}
                    as='a'
                    {...linkProps}
                  >
                    Back
                  </Button>
                )}
              </Link>

              <Box display={['block', 'block', 'none']}>
                <Button
                  variant='major'
                  onClick={handleOnboardingFinished}
                  data-test-id='extension-mobile-skip-button'
                >
                  Download later
                </Button>
              </Box>
              <Box gap={4} display={['none', 'none', 'flex']}>
                <Button
                  variant='moderate'
                  onClick={handleOnboardingFinished}
                  data-test-id='extension-skip-button'
                >
                  Skip for now
                </Button>
                <Button
                  variant='major'
                  startContent={<Button.StartContent variant='icon' icon={IconGoogle} />}
                  as='a'
                  href={chromeExtensionWebStoreUrl}
                  target='_blank'
                  rel='noreferrer'
                  onClick={() => {
                    track(
                      ChilicalEvent.OnboardingChromeExtensionInstallClicked,
                      ChilicalFeature.Onboarding
                    )
                    handleOnboardingFinished()
                  }}
                >
                  Install Chrome Extension
                </Button>
              </Box>
            </Flex>
            <Progress totalSteps={5 + extraSteps} currentStep={5} />
          </Flex>
        </Box>
        <Spacer />
      </StyledFlex>

      <Box
        display={['none', 'none', 'flex']}
        alignItems='center'
        justifyContent='flex-end'
        flexBasis='50%'
      >
        <Box as='img' src={features[selectedFeature].image} alt='' maxHeight='90%' maxWidth='90%' />
      </Box>
    </Flex>
  )
}

const StyledFlex = styled(Flex)`
  ${({ theme }) => css`
    @media (min-width: ${theme.breakpoints[1]}) {
      mask: url('${starMask}') no-repeat top right;
    }
  `}
`
const StyledBox = styled(Box)`
  line-height: 0;
`
